import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

export const chartPalette = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948']
export const longListColor = '#64748b'
export const notSpecifiedColor = '#cbd5e1'
export const notSpecifiedKey = 'not_specified'
export const notAssignedKey = 'not_assigned'
export const mutedPillStyle = { color: '#64748b', backgroundColor: '#f8fafc' }

export const toItems = (counts, labelMap, colorMap = {}) => {
  const safeCounts = counts || {}
  const safeLabels = labelMap || {}
  const keys = Object.keys(safeLabels)
  const isLongList = keys.length > chartPalette.length
  const items = keys.map((key, index) => ({
    key,
    label: safeLabels[key],
    value: safeCounts[key] || 0,
    color: colorMap[key] || (isLongList ? longListColor : chartPalette[index])
  }))
  items.push({ key: notSpecifiedKey, label: 'Not specified', value: safeCounts[notSpecifiedKey] || 0, color: notSpecifiedColor })
  return items.filter(item => item.value > 0)
}

export const rankedItems = (items) => [...items].sort((a, b) => (a.key === notSpecifiedKey) - (b.key === notSpecifiedKey) || b.value - a.value)

export const listMax = (items) => Math.max(1, ...items.map(item => item.value))

const percent = (value, total) => total > 0 ? Math.round(value * 100 / total) : 0

export const percentLabel = (value, total) => {
  const rounded = percent(value, total)
  if (rounded > 0 || value <= 0) return rounded + '%'
  return '<1%'
}

export const shareWidth = (value, total) => total > 0 ? (value * 100 / total) + '%' : '0%'

export const pctStyle = (item) => item.key === notSpecifiedKey ? mutedPillStyle : {}

export const recordedCount = (items) => items.filter(item => item.key !== notSpecifiedKey).reduce((sum, item) => sum + item.value, 0)

export const hasData = (items) => recordedCount(items) > 0

export const donutStyle = (items, total) => {
  if (total === 0) return { background: notSpecifiedColor }
  let start = 0
  const stops = items.map(item => {
    const end = start + item.value * 360 / total
    const stop = `${item.color} ${start}deg ${end}deg`
    start = end
    return stop
  })
  return { background: `conic-gradient(${stops.join(', ')})` }
}

export const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('')

export const genderColors = { male: '#2a78d6', female: '#e87ba4', others: '#eda100' }
export const ageColors = { ag_1: '#5eead4', ag_2: '#2dd4bf', ag_3: '#14b8a6', ag_4: '#0d9488', ag_5: '#0f766e', ag_6: '#115e59', ag_7: '#134e4a', ag_8: '#042f2e' }
export const profileStatusLabels = { complete: 'Complete', partial: 'Partial', incomplete: 'Incomplete' }

export const profileStatusOf = (filled, fieldCount) => {
  if (filled === fieldCount) return 'complete'
  if (filled >= Math.ceil(fieldCount / 2)) return 'partial'
  return 'incomplete'
}

export const profileStatusCounts = (profileCounts, fieldCount) => Object.keys(profileCounts || {}).reduce((counts, key) => {
  counts[profileStatusOf(Number(key.replace('f_', '')), fieldCount)] += profileCounts[key]
  return counts
}, { complete: 0, partial: 0, incomplete: 0 })

export const useHrWorkforceItems = (current, labels, summary) => {
  const categoryItems = computed(() => toItems(current.value.category, labels.value.categories))
  const genderItems = computed(() => toItems(current.value.gender, labels.value.genders, genderColors))
  const religionItems = computed(() => toItems(current.value.religion, labels.value.religions))
  const jobStatusItems = computed(() => toItems(current.value.job_status, labels.value.job_statuses))
  const departmentItems = computed(() => toItems(current.value.department, labels.value.departments))
  const designationItems = computed(() => toItems(current.value.designation, labels.value.designations))
  const maritalItems = computed(() => toItems(current.value.marital_status, labels.value.marital_statuses))
  const tenureItems = computed(() => toItems(current.value.tenure, labels.value.tenures))
  const divisionItems = computed(() => toItems(current.value.division, labels.value.divisions))
  const ageItems = computed(() => toItems(current.value.age, labels.value.age_ranges, ageColors))
  const categoryLegend = computed(() => summary && summary.value ? toItems(summary.value.all.category, labels.value.categories) : [])

  const donuts = computed(() => [
    { title: 'Gender of staff', items: genderItems.value },
    { title: 'Employment type', items: categoryItems.value },
    { title: 'Religion', items: religionItems.value },
    { title: 'Marital status', items: maritalItems.value }
  ])

  const rankedLists = computed(() => [
    { title: 'Department', items: rankedItems(departmentItems.value) },
    { title: 'Designation', items: rankedItems(designationItems.value) },
    { title: 'Tenure', items: tenureItems.value }
  ])

  const visibleDonuts = computed(() => donuts.value.filter(donut => hasData(donut.items)))
  const visibleRankedLists = computed(() => rankedLists.value.filter(list => hasData(list.items)))

  const panelGroups = computed(() => [
    { title: 'Employment type', items: categoryItems.value },
    { title: 'Gender', items: genderItems.value },
    { title: 'Religion', items: religionItems.value },
    { title: 'Marital status', items: maritalItems.value },
    { title: 'Age', items: ageItems.value },
    { title: 'Department', items: rankedItems(departmentItems.value) },
    { title: 'Designation', items: rankedItems(designationItems.value) },
    { title: 'Job status', items: jobStatusItems.value },
    { title: 'Tenure', items: tenureItems.value }
  ].filter(group => hasData(group.items)))

  return {
    categoryItems,
    genderItems,
    religionItems,
    jobStatusItems,
    departmentItems,
    designationItems,
    maritalItems,
    tenureItems,
    divisionItems,
    ageItems,
    categoryLegend,
    donuts,
    rankedLists,
    visibleDonuts,
    visibleRankedLists,
    panelGroups
  }
}

export const useHrWorkforcePagedRows = (rows, page, pageSize) => {
  const pageCount = computed(() => Math.max(1, Math.ceil(rows.value.length / pageSize)))
  const currentPage = computed(() => Math.min(page.value, pageCount.value))
  const pageStart = computed(() => (currentPage.value - 1) * pageSize)
  const pageEnd = computed(() => Math.min(pageStart.value + pageSize, rows.value.length))
  const pagedRows = computed(() => rows.value.slice(pageStart.value, pageEnd.value))
  const pager = computed(() => ({ start: pageStart.value, end: pageEnd.value, total: rows.value.length, page: currentPage.value, page_count: pageCount.value }))
  return { pagedRows, pager }
}

export const matchesSearch = (row, search) => {
  if (search === '') return true
  return String(row.name).toLowerCase().includes(search) || String(row.serial || '').toLowerCase().includes(search)
}

export const useHrWorkforceEmployeeFilters = (filters, props, emit) => {
  const activeSubTab = ref('overview')
  const searchText = ref('')
  const page = ref(1)

  const resetFilters = () => {
    Object.keys(filters).forEach(key => {
      filters[key] = ''
    })
    searchText.value = ''
    page.value = 1
  }

  const applyChartFilter = (key, value) => {
    const isActive = filters[key] === value
    resetFilters()
    filters[key] = isActive ? '' : value
    activeSubTab.value = isActive ? activeSubTab.value : 'employees'
  }

  const selectBranch = (key) => {
    activeSubTab.value = 'employees'
    emit('select-branch', key)
  }

  const clearChip = (key) => {
    if (key === 'branch') return emit('select-branch', 'all')
    if (key === 'search') searchText.value = ''
    filters[key] = ''
    page.value = 1
  }

  const clearAll = () => {
    resetFilters()
    if (props.selectedBranch !== 'all') emit('select-branch', 'all')
  }

  const onSearch = (value) => {
    searchText.value = value
    filters.search = value.trim()
    page.value = 1
  }

  watch(() => props.selectedBranch, () => {
    page.value = 1
  })

  return { activeSubTab, searchText, page, applyChartFilter, selectBranch, clearChip, clearAll, onSearch }
}

export const useHrWorkforceEmployeeProfile = () => {
  const $store = useStore()
  const $route = useRoute()
  const $router = useRouter()

  const employeePage = computed(() => {
    const menus = $store.getters['company/getSidebar'] || []
    const pages = menus.flatMap(menu => (menu.submenus || []).flatMap(submenu => (submenu.pages || []).map(item => ({ menu_id: menu.id, page_id: item.id, route: item.route }))))
    return pages.find(item => item.route === 'employee-register') || null
  })

  const openProfile = (employee) => {
    if (!employeePage.value) return
    const resolved = $router.resolve({
      name: 'add-employee',
      params: {
        companyId: $route.params.companyId,
        moduleId: $route.params.moduleId,
        menuId: employeePage.value.menu_id,
        pageId: employeePage.value.page_id
      },
      query: { contactId: employee.id, type: employee.type },
      hash: employee.type === 'person' ? '#basic-info' : '#org-basic-info'
    })
    window.open(resolved.href, '_blank')
  }

  return { employeePage, openProfile }
}
